"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/platform/auth/useAuth";
import { createEvaluation } from "@/platform/firebase/firestore/supplier-evaluation";
import { suggestEvaluationFocus } from "../api";
import { EVALUATION_CATEGORIES, INDUSTRIES } from "../types";

export function NewEvaluation() {
  const router = useRouter();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [customCategories, setCustomCategories] = useState<string[]>([]);
  const [aiCategories, setAiCategories] = useState<{ label: string; checked: boolean }[]>([]);
  const [newCategory, setNewCategory] = useState("");

  const [form, setForm] = useState({
    companyName: "",
    contactPerson: "",
    email: "",
    industry: "",
    customIndustry: "",
    evaluationFocus: EVALUATION_CATEGORIES.map(() => true),
    additionalContext: "",
  });

  const selectedCount =
    form.evaluationFocus.filter(Boolean).length +
    customCategories.length +
    aiCategories.filter((c) => c.checked).length;

  const handleGenerateCategories = async () => {
    if (!form.industry) return;
    setAiLoading(true);
    try {
      const industry = form.industry === "other" ? form.customIndustry : form.industry;
      const categories = await suggestEvaluationFocus(industry);
      setForm((f) => ({ ...f, evaluationFocus: EVALUATION_CATEGORIES.map(() => false) }));
      setCustomCategories([]);
      setAiCategories(categories.map((label) => ({ label, checked: false })));
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const selected = [
        ...EVALUATION_CATEGORIES.filter((_, i) => form.evaluationFocus[i]),
        ...customCategories,
        ...aiCategories.filter((c) => c.checked).map((c) => c.label),
      ];
      const finalIndustry = form.industry === "other" ? form.customIndustry : form.industry;
      const id = await createEvaluation(user.uid, user.email!, {
        project_name: `${form.companyName} Evaluation`,
        supplier_info: {
          company_name: form.companyName,
          contact_person: form.contactPerson,
          email: form.email,
          industry: finalIndustry,
        },
        evaluation_focus: selected,
        additional_context: form.additionalContext || undefined,
      });
      router.push(`/agentic-workflows/supplier-evaluation/${id}`);
    } finally {
      setLoading(false);
    }
  };

  const isValid =
    form.companyName.trim() &&
    form.contactPerson.trim() &&
    form.email.includes("@") &&
    form.industry &&
    (form.industry !== "other" || form.customIndustry.trim());

  return (
    <>
      <section className="diag-hero">
        <h1 style={{ fontSize: "clamp(28px,4vw,48px)", fontWeight: 800, letterSpacing: "-0.03em", margin: "0 0 8px" }}>
          New Supplier Evaluation
        </h1>
        <p className="diag-hero-sub">Enter supplier details and select evaluation focus areas</p>
      </section>

      <div className="diag-page" style={{ maxWidth: "760px" }}>
        {/* Back */}
        <button
          onClick={() => router.push("/agentic-workflows/supplier-evaluation")}
          style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", color: "var(--ink-mute)", fontSize: "13px", cursor: "pointer", marginBottom: "24px", padding: 0 }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
          Back to evaluations
        </button>

        <div className="diag-card" style={{ gap: "20px" }}>
          <div>
            <h2 style={{ margin: "0 0 4px", fontSize: "17px", fontWeight: 600 }}>Supplier Information</h2>
            <p style={{ fontSize: "13px", color: "var(--ink-mute)", margin: 0 }}>Enter supplier details and select evaluation focus areas</p>
          </div>

          {/* Grid fields */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            {[
              { id: "companyName",    label: "Company Name *",   type: "text",  key: "companyName" },
              { id: "contactPerson",  label: "Contact Person *", type: "text",  key: "contactPerson" },
              { id: "email",          label: "Email *",          type: "email", key: "email" },
            ].map(({ id, label, type, key }) => (
              <div key={id} style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <label htmlFor={id} style={labelStyle}>{label}</label>
                <input
                  id={id} type={type}
                  value={form[key as keyof typeof form] as string}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  style={inputStyle}
                />
              </div>
            ))}

            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label htmlFor="industry" style={labelStyle}>Industry *</label>
              <select
                id="industry"
                value={form.industry}
                onChange={(e) => setForm({ ...form, industry: e.target.value, customIndustry: "" })}
                style={inputStyle}
              >
                <option value="">Select industry</option>
                {INDUSTRIES.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
              </select>
              {form.industry === "other" && (
                <input
                  type="text"
                  placeholder="Enter industry name"
                  value={form.customIndustry}
                  onChange={(e) => setForm({ ...form, customIndustry: e.target.value })}
                  style={{ ...inputStyle, marginTop: "6px" }}
                />
              )}
            </div>
          </div>

          {/* Evaluation focus */}
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
              <label style={labelStyle}>Evaluation Focus ({selectedCount} selected)</label>
              <button
                type="button"
                onClick={handleGenerateCategories}
                disabled={!form.industry || (form.industry === "other" && !form.customIndustry) || aiLoading}
                style={ghostBtnStyle}
              >
                {aiLoading ? "Generating…" : "✨ Generate using AI"}
              </button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              {EVALUATION_CATEGORIES.map((cat, i) => (
                <label key={cat} style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "13px", color: "var(--ink-soft)" }}>
                  <input
                    type="checkbox"
                    checked={form.evaluationFocus[i]}
                    onChange={(e) => {
                      const next = [...form.evaluationFocus];
                      next[i] = e.target.checked;
                      setForm({ ...form, evaluationFocus: next });
                    }}
                  />
                  {cat}
                </label>
              ))}
              {aiCategories.map((cat, i) => (
                <label key={`ai-${i}`} style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "13px", color: "var(--ink-soft)" }}>
                  <input
                    type="checkbox"
                    checked={cat.checked}
                    onChange={(e) => {
                      const updated = [...aiCategories];
                      updated[i] = { ...updated[i], checked: e.target.checked };
                      setAiCategories(updated);
                    }}
                  />
                  {cat.label} <span style={{ fontSize: "11px", color: "#A855F7" }}>✨ AI</span>
                </label>
              ))}
              {customCategories.map((cat, i) => (
                <label key={`custom-${i}`} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "var(--ink-soft)" }}>
                  <input type="checkbox" checked disabled />
                  {cat}
                  <button onClick={() => setCustomCategories(customCategories.filter((_, j) => j !== i))} style={{ background: "none", border: "none", color: "var(--ink-mute)", cursor: "pointer", padding: 0, fontSize: "14px" }}>×</button>
                </label>
              ))}
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input type="checkbox" checked={false} disabled />
                <input
                  type="text"
                  placeholder="Add custom category…"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newCategory.trim()) {
                      setCustomCategories([...customCategories, newCategory.trim()]);
                      setNewCategory("");
                    }
                  }}
                  style={{ ...inputStyle, flex: 1 }}
                />
              </div>
            </div>
          </div>

          {/* Additional context */}
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={labelStyle}>Additional Context (Optional)</label>
            <textarea
              rows={3}
              placeholder="Any specific requirements or concerns…"
              value={form.additionalContext}
              onChange={(e) => setForm({ ...form, additionalContext: e.target.value })}
              style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
            />
          </div>

          {/* Actions */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", paddingTop: "8px", borderTop: "1px solid var(--line)" }}>
            <button onClick={() => router.push("/agentic-workflows/supplier-evaluation")} style={ghostBtnStyle}>
              Cancel
            </button>
            <button onClick={handleSubmit} disabled={loading || !isValid} style={primaryBtnStyle}>
              {loading ? "Creating…" : "Next: Generate Questions →"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

const labelStyle: React.CSSProperties = { fontSize: "13px", fontWeight: 600, color: "var(--ink-soft)" };
const inputStyle: React.CSSProperties = {
  background: "var(--paper-3)", border: "1px solid var(--line-strong)",
  borderRadius: "8px", padding: "9px 12px", fontSize: "13px", color: "var(--ink)", width: "100%",
};
const primaryBtnStyle: React.CSSProperties = {
  background: "linear-gradient(135deg, var(--indigo), var(--magenta))",
  border: "none", borderRadius: "8px", padding: "10px 20px",
  fontSize: "13px", fontWeight: 600, color: "#fff", cursor: "pointer",
};
const ghostBtnStyle: React.CSSProperties = {
  background: "var(--paper-2)", border: "1px solid var(--line-strong)",
  borderRadius: "8px", padding: "9px 16px", fontSize: "13px",
  color: "var(--ink-soft)", cursor: "pointer",
};
